import React from "react";
import { motion } from "framer-motion";
import { Heart, Building2, Music, Trophy, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/constants/testIds";
import { MEDIA } from "@/lib/site";

const CARDS = [
  {
    id: "mariage",
    title: "Mariages & Cérémonies",
    desc: "Des shows aériens intimistes ou grandioses pour graver l'instant à jamais.",
    Icon: Heart,
    image: MEDIA.wedding,
    span: "md:col-span-8 lg:col-span-7 md:row-span-2",
    tall: true,
  },
  {
    id: "corporate",
    title: "Corporate & Brand",
    desc: "Lancements de marque, soirées, conférences. Logos, slogans, signatures dans le ciel.",
    Icon: Building2,
    image: MEDIA.corporate,
    span: "md:col-span-4 lg:col-span-5",
  },
  {
    id: "festivals",
    title: "Festivals & Villes",
    desc: "Productions monumentales jusqu'à 800 drones pour fêtes de villes et festivals.",
    Icon: Music,
    image: MEDIA.festival,
    span: "md:col-span-4 lg:col-span-5",
  },
  {
    id: "sport",
    title: "Sport & Événementiel",
    desc: "Ouvertures, cérémonies, finales. Une dramaturgie aérienne sur-mesure.",
    Icon: Trophy,
    image: MEDIA.sport,
    span: "md:col-span-4 lg:col-span-5",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="relative py-28 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
              Nos univers
            </div>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
              Chaque <span className="italic font-semibold">événement</span><br />
              mérite son <span className="text-[#007AFF]">ciel</span>.
            </h2>
          </div>
          <p className="text-white/60 max-w-md">
            Quatre territoires d'expression. Une signature commune : précision technique,
            écriture artistique et impact émotionnel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:auto-rows-[220px]">
          {CARDS.map((c, idx) => (
            <motion.button
              key={c.id}
              data-testid={SERVICES.card(c.id)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
              whileHover={{ scale: 1.015 }}
              className={`group relative col-span-12 ${c.span} overflow-hidden rounded-3xl glass-strong text-left`}
              style={{ minHeight: c.tall ? 460 : 220 }}
            >
              <img
                src={c.image}
                alt={`${c.title} — spectacle de drones lumineux signé drones3x`}
                className="absolute inset-0 h-full w-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-transparent" />

              <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-full glass-strong">
                    <c.Icon size={18} className="text-[#007AFF]" />
                  </div>
                  <ArrowUpRight size={18} className="text-white/60 group-hover:text-[#007AFF] group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
                </div>

                <div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-white/70 max-w-md leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
